import React, { useEffect, useMemo, useState } from 'react';
import { FaCheckCircle, FaExclamationTriangle, FaSearch, FaClipboardCheck, FaPrint } from 'react-icons/fa';
import { Link, useSearchParams } from 'react-router-dom';
import { printAssessmentReport, gapAssessmentReportHTML } from '../utils/assessmentReports';
import { apiMasterAPI, equipmentAPI, productsAPI, samplingPlansAPI, settingsAPI, resultsAPI } from '../services/api';
import { buildCleaningValidationCoverage } from '../utils/cleaningValidationCoverage';

const getRows = (response) => {
  const data = response?.data?.data;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.rows)) return data.rows;
  return [];
};

const GapAssessment = () => {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [trains, setTrains] = useState([]);
  const [apis, setApis] = useState([]);
  const [products, setProducts] = useState([]);
  const [plans, setPlans] = useState([]);
  const [settings, setSettings] = useState({});
  const [manualSoilingEntries, setManualSoilingEntries] = useState([]);
  const [results, setResults] = useState([]);
  const [trainMembersById, setTrainMembersById] = useState({});
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [view, setView] = useState(searchParams.get('view') || 'gaps');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setLoadError('');
        const [settingsRes, trainsRes, apisRes, productsRes, plansRes, resultsRes] = await Promise.all([
          settingsAPI.getAll(),
          equipmentAPI.getTrains(),
          apiMasterAPI.getAll({ limit: 5000 }),
          productsAPI.getAll({ limit: 5000 }),
          samplingPlansAPI.getAll(),
          resultsAPI.getAll()
        ]);
        const settingsData = settingsRes?.data?.data || {};
        const loadedTrains = getRows(trainsRes);
        const members = await Promise.all(loadedTrains.map(async (train) => {
          try {
            return [String(train.train_id), getRows(await equipmentAPI.getTrainMembers(train.train_id))];
          } catch {
            return [String(train.train_id), []];
          }
        }));
        if (cancelled) return;
        setSettings(settingsData);
        setTrains(loadedTrains);
        setApis(getRows(apisRes));
        setProducts(getRows(productsRes));
        setPlans(getRows(plansRes));
        setResults(getRows(resultsRes));
        setTrainMembersById(Object.fromEntries(members));
        try {
          const parsed = JSON.parse(settingsData.worst_case_manual_soiling_entries || '[]');
          setManualSoilingEntries(Array.isArray(parsed) ? parsed : []);
        } catch {
          setManualSoilingEntries([]);
        }
      } catch (error) {
        if (!cancelled) setLoadError(error?.response?.data?.message || 'Could not load gap assessment data.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const coverage = useMemo(() => buildCleaningValidationCoverage({
    trains, apis, products, plans, trainMembersById, manualSoilingEntries, rankingConfig: settings, results
  }), [trains, apis, products, plans, trainMembersById, manualSoilingEntries, settings, results]);

  const visibleRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    return coverage.filter((row) => {
      if (view === 'gaps' && row.gaps.length === 0) return false;
      if (view === 'covered' && row.gaps.length > 0) return false;
      if (!query) return true;
      const haystack = [
        row.train.train_name, row.train.train_code,
        row.worstCase?.api_name, row.worstCase?.product_name,
        ...row.gaps,
        ...row.missingEquipment.map((item) => item.equipment_name || item.equipment_code)
      ].join(' ').toLowerCase();
      return haystack.includes(query);
    });
  }, [coverage, search, view]);

  const coveredCount = coverage.filter((row) => row.gaps.length === 0).length;
  const gapCount = coverage.length - coveredCount;
  const manualCount = coverage.reduce((sum, row) => sum + row.soilingForTrain.length, 0);

  if (loading) return <div className="loading-container"><div className="spinner" /></div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '14px', marginBottom: '18px' }}>
        <div>
          <h1 style={{ margin: '0 0 5px', fontSize: 'var(--fs-xl)' }}>Cleaning Validation Gap Assessment</h1>
          <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
            Review worst-case and approved sampling-plan coverage for each train, including equipment and applicable parts.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <Link className="btn btn-outline" to="/equipment-trains">Equipment Trains</Link>
          <Link className="btn btn-outline" to="/worst-case">Worst Case</Link>
          <Link className="btn btn-primary" to="/acceptance-criteria">Sampling Plans</Link>
          <Link className="btn btn-outline" to="/cleaning-validation-settings">Manual Contamination</Link>
          <button type="button" className="btn btn-outline" disabled={visibleRows.length === 0} onClick={() => printAssessmentReport({ reportKey: 'gap_assessment',
            title: 'Cleaning Validation Gap Assessment',
            documentName: 'Cleaning Validation Gap Assessment',
            build: () => gapAssessmentReportHTML(visibleRows, [{ gaps: 'Trains with gaps', all: 'All trains', covered: 'Covered' }[view], search.trim()].filter(Boolean).join(' / ')),
          })}>
            <FaPrint /> Print / PDF
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '12px', marginBottom: '16px' }}>
        <div className="card" style={{ padding: '14px 16px' }}><div className="text-muted">Equipment Trains</div><strong style={{ fontSize: 'var(--fs-xl)' }}>{coverage.length}</strong></div>
        <div className="card" style={{ padding: '14px 16px', borderLeft: '4px solid var(--success-color)' }}><div className="text-muted">Plan Coverage Complete</div><strong style={{ fontSize: 'var(--fs-xl)', color: 'var(--success-text)' }}>{coveredCount}</strong></div>
        <div className="card" style={{ padding: '14px 16px', borderLeft: '4px solid var(--warning-color)' }}><div className="text-muted">Trains With Gaps</div><strong style={{ fontSize: 'var(--fs-xl)', color: 'var(--warning-text)' }}>{gapCount}</strong></div>
        <div className="card" style={{ padding: '14px 16px' }}><div className="text-muted">Manual Contamination Entries</div><strong style={{ fontSize: 'var(--fs-xl)' }}>{manualCount}</strong></div>
      </div>

      <div className="card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><FaClipboardCheck /><strong>Train Coverage Register</strong></div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ position: 'relative' }}><FaSearch style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} /><input className="form-control" style={{ paddingLeft: '30px', minWidth: '230px' }} placeholder="Search train, API, equipment..." value={search} onChange={(event) => setSearch(event.target.value)} /></div>
            <select className="form-control" style={{ width: 'auto' }} value={view} onChange={(event) => setView(event.target.value)}>
              <option value="gaps">Trains with gaps</option><option value="all">All trains</option><option value="covered">Covered</option>
            </select>
          </div>
        </div>
        {loadError && <div className="alert alert-danger" style={{ margin: '12px' }}>{loadError}</div>}
        {!loadError && coverage.length === 0 ? <div className="text-muted" style={{ padding: '16px' }}>No equipment trains found for the selected unit.</div> : (
          <div className="table-container">
            <table>
              <thead><tr><th>Train</th><th>Equipment</th><th>Worst Case</th><th>CV Sampling Plan</th><th>Locations / Gaps</th><th>Manual Contamination</th><th>Status</th><th>Next Action</th></tr></thead>
              <tbody>
                {visibleRows.length === 0 ? <tr><td colSpan={8} className="text-center text-muted">No trains match this filter.</td></tr> : visibleRows.map((row) => (
                  <tr key={row.train.train_id}>
                    <td>
                      <Link to="/equipment-trains" state={{ openViewTrainId: row.train.train_id }} className="record-link" title="View train">
                        <strong>{row.train.train_name || row.train.train_code}</strong>
                      </Link>
                      <div className="text-muted" style={{ fontSize: 'var(--fs-xs)' }}>{row.train.train_code}</div>
                    </td>
                    <td>{row.members.length} item(s){row.missingEquipment.length > 0 && <div style={{ color: 'var(--warning-text)', fontSize: 'var(--fs-xs)', marginTop: '3px' }}>{row.missingEquipment.map((member) => member.equipment_name || member.equipment_code).join(', ')}</div>}</td>
                    <td>{row.worstCase && !row.worstCase.is_provisional ? <>{row.worstCase.product_name}<div className="text-muted" style={{ fontSize: 'var(--fs-xs)' }}>API: {row.worstCase.api_name}</div></> : <span style={{ color: 'var(--warning-text)' }}>No approved worst case</span>}</td>
                    <td>{row.worstCaseCvPlans.length > 0
                      ? row.worstCaseCvPlans.map((plan) => <div key={plan.plan_id}>{plan.plan_code} <span className="text-muted">(Approved)</span></div>)
                      : row.allCvPlans.length > 0
                        ? row.allCvPlans.map((plan) => <div key={plan.plan_id}>{plan.plan_code} <span className="text-muted">({plan.status || 'Draft'})</span></div>)
                        : <span style={{ color: 'var(--warning-text)' }}>No CV plan</span>}</td>
                    <td>{row.gaps.length === 0
                      ? <span style={{ color: 'var(--success-text)' }}>All linked equipment/parts have locations</span>
                      : <ul style={{ margin: 0, paddingLeft: '18px' }}>{row.gaps.map((gap) => <li key={gap} style={{ color: 'var(--warning-text)' }}>{gap}</li>)}</ul>}</td>
                    <td>{row.soilingForTrain.length ? `${row.soilingForTrain.length} logged` : <span className="text-muted">None</span>}<div className="text-muted" style={{ fontSize: 'var(--fs-xs)' }}>Tracked separately; does not close CV gap</div></td>
                    <td>{row.gaps.length === 0
                      ? <span style={{ color: 'var(--success-text)', display: 'inline-flex', gap: '5px', alignItems: 'center' }}><FaCheckCircle /> Plan coverage complete</span>
                      : <span style={{ color: 'var(--warning-text)', display: 'inline-flex', gap: '5px', alignItems: 'center' }}><FaExclamationTriangle /> Gap</span>}</td>
                    <td><div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      {(!row.worstCase || row.worstCase.is_provisional) && <Link className="btn btn-outline btn-sm" to="/worst-case">Set worst case</Link>}
                      {(!row.worstCaseCvPlans.length || row.missingEquipment.length > 0) && <Link className="btn btn-primary btn-sm" to="/acceptance-criteria">Complete plan</Link>}
                    </div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div style={{ padding: '12px 14px', borderTop: '1px solid var(--border-color)', background: 'var(--info-bg)', color: 'var(--info-text)', fontSize: 'var(--fs-sm)', lineHeight: 1.5 }}>
          <strong>What “Plan coverage complete” checks:</strong> an approved plan matches the train’s worst-case API, has a sample location for every linked equipment item/part, every one of those locations has an approved acceptance limit, and at least one result has been recorded against it.
          <br /><strong>What it doesn’t check:</strong> whether the required number of validation batches/intervals is complete — see Sampling Plans for that. Manual contamination entries are tracked separately and don’t close a CV gap on their own.
        </div>
      </div>
    </div>
  );
};

export default GapAssessment;
